import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { MODULES } from "@/lib/modules";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { DataLayer } from "@/components/DataLayer";
import { JusticeFramework } from "@/components/JusticeFramework";
import { CapabilityBoard } from "@/components/CapabilityBoard";
import { Lock, ArrowRight, Eye, Satellite, ShieldCheck, GraduationCap, Sparkles } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1557264337-e8a93017fe92?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Stat = ({ value, label }) => (
  <div className="qs-panel p-5">
    <div className="font-mono text-2xl font-bold text-[#00FFFF] whitespace-nowrap">{value}</div>
    <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
  </div>
);

export default function Landing() {
  const { t, lang } = useLang();
  const [registry, setRegistry] = useState(null);
  useEffect(() => { api.get("/web3/registry").then((r) => setRegistry(r.data)).catch(() => setRegistry({ configured: false })); }, []);
  const ledgerChip = registry?.configured ? { s: `Simulated ledger · term-sheet registry live on ${registry.chain_name}`, tone: "#00FF7F" } : { s: "Simulated ledger · registry not deployed", tone: "#F59E0B" };

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-[#050A15]/70 qs-noise" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-24 md:pt-28 md:pb-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="qs-panel p-5 max-w-3xl mb-8 border-l-2 border-l-[#00FF7F]" data-testid="plain-language">
              <p className="text-[10px] uppercase tracking-widest text-[#00FF7F] font-mono">{t("plain_title")}</p>
              <p className="text-sm md:text-base text-slate-200 mt-2 leading-relaxed">{t("plain_body")}</p>
              <p className="text-xs text-slate-400 mt-2">{t("plain_who")} <a href="#capabilities" className="text-[#00FFFF]" data-testid="plain-capabilities-link">{lang === "tr" ? "Hangi özellikler canlı, simüle, planlı →" : "Which features are live, mocked or planned →"}</a></p>
            </div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00FFFF]/30 bg-[#00FFFF]/5 text-[#00FFFF] text-xs font-mono uppercase tracking-widest" data-testid="hero-tag">
              <Eye className="h-3.5 w-3.5" /> {t("hero_tag")}
            </span>
            <h1 className="font-display font-extrabold tracking-tighter mt-6 text-4xl sm:text-5xl lg:text-6xl max-w-5xl leading-[1.04]" data-testid="hero-title">
              <span className="qs-grad-text">{t("hero_title")}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed" data-testid="hero-sub">{t("hero_sub")}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/housing" data-testid="hero-housing-btn"
                className="qs-cta inline-flex items-center gap-2 px-6 py-3 bg-[#00FFFF] text-[#050A15] font-semibold">
                {t("hero_cta3")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/register" data-testid="hero-launch-btn"
                className="qs-cta inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-slate-100 hover:border-[#00FFFF]/50">
                {t("hero_cta1")}
              </Link>
              <a href="#partner" data-testid="hero-demo-btn"
                className="qs-cta inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-slate-100 hover:border-[#00FF7F]/50">
                {t("hero_cta2")}
              </a>
            </div>
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl" data-testid="hero-stats">
              <Stat value="7 codes" label="TR 1998 · 2007 · 2018 · 2027 draft · USA · Japan · Chile" />
              <Stat value="85 / 6" label="Criteria · pillars, auto-scored 1–5" />
              <Stat value="Term sheet" label="Editable PDF · anchored digest · QR verification" />
              <Stat value="Sentinel-2" label="Live STAC · HSI uploads" />
            </div>
          </motion.div>
        </div>
      </section>

      <DataLayer />

      <CapabilityBoard />

      {/* MODULES BENTO */}
      <section id="modules" className="mx-auto max-w-7xl px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{t("modules_title")}</h2>
            <p className="text-slate-400 mt-2 max-w-xl">{t("modules_sub")}</p>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#00FF7F]" /> {t("live")}</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-slate-600" /> {t("soon")}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            const span = m.live ? "md:col-span-6" : "md:col-span-4";
            return (
              <motion.div key={m.id}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                className={span}>
                <ModuleCard m={m} Icon={Icon} lang={lang} liveLabel={t("live")} soonLabel={t("soon")} />
              </motion.div>
            );
          })}
        </div>
      </section>

      <JusticeFramework />

      {/* ACADEMIC */}
      <section id="academic" className="mx-auto max-w-7xl px-6 py-16">
        <div className="qs-grad-border p-8 md:p-12 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <span className="inline-flex items-center gap-2 text-[#00FF7F] text-xs font-mono uppercase tracking-widest">
              <GraduationCap className="h-4 w-4" /> {t("academic_title")}
            </span>
            <h3 className="font-display font-bold text-2xl md:text-3xl mt-4">Başkent University</h3>
            <p className="text-slate-400 mt-3 leading-relaxed max-w-xl">{t("academic_body")}</p>
          </div>
          <div className="flex md:justify-end">
            <div className="grid place-items-center h-28 w-28 rounded-full border border-white/10 bg-[#0A1128]">
              <GraduationCap className="h-12 w-12 text-[#00FFFF]" />
            </div>
          </div>
        </div>
      </section>

      {/* IMAGING & QUANTUM LAYER — STATUS */}
      <section id="imaging-quantum" className="mx-auto max-w-7xl px-6 py-8" data-testid="layer-status">
        <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{t("layer_title")}</h2>
        <p className="text-slate-400 mt-2 max-w-2xl text-sm">{t("layer_sub")}</p>
        <div className="grid md:grid-cols-3 gap-5 mt-6">
        {[
          { icon: Satellite, t: "Satellite + Hyperspectral", s: "Available", tone: "#00FF7F", d: "Direct Sentinel-2 ingestion (open STAC) plus ENVI / GeoTIFF / .mat uploads; indices and classification run on the server. 224-band field cameras are not connected in this build." },
          { icon: ShieldCheck, t: "Verifiable & Transparent", s: ledgerChip.s, tone: ledgerChip.tone, d: `Every verified scan and matrix carries a SHA-256 anchor with a public verification link. Record anchors live in the database (no chain write); housing term-sheet digests ${registry?.configured ? `can be written by authors to the TermSheetRegistry contract on ${registry.chain_name} and are read back on /verify` : "can be written to the TermSheetRegistry contract once an admin deploys it"}. A match proves the document is unchanged, not that its claims are correct.` },
          { icon: Sparkles, t: "Quantum-Pluggable AI", s: "Mocked until IBM token", tone: "#F59E0B", d: "Quantum SVM / QAOA with automatic classical fallback — runs on the local Qiskit simulator until an IBM Quantum token is added. No quantum advantage is assumed; results are benchmarked against classical baselines." },
        ].map((p) => (
          <div key={p.t} className="qs-panel qs-lift p-6" data-testid={`layer-card-${p.t.split(" ")[0].toLowerCase()}`}>
            <div className="flex items-center justify-between gap-2"><p.icon className="h-6 w-6 text-[#00FFFF]" /><span className="text-[10px] uppercase tracking-wider border px-2 py-0.5" style={{ color: p.tone, borderColor: `${p.tone}66` }}>{p.s}</span></div>
            <h4 className="font-display font-semibold text-lg mt-4">{p.t}</h4>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{p.d}</p>
          </div>
        ))}
        </div>
      </section>

      {/* PARTNER CTA */}
      <section id="partner" className="mx-auto max-w-7xl px-6 py-20">
        <div className="relative overflow-hidden qs-panel p-10 md:p-16 text-center">
          <div className="absolute inset-0 qs-noise opacity-60" />
          <div className="relative">
            <h3 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">{t("cta_partner_title")}</h3>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto">{t("cta_partner_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Link to="/register" data-testid="partner-signup-btn"
                className="qs-cta px-6 py-3 bg-[#00FF7F] text-[#050A15] font-semibold">{t("hero_cta1")}</Link>
              <Link to="/marketplace" data-testid="partner-invest-btn"
                className="qs-cta px-6 py-3 border border-white/15 hover:border-[#00FFFF]/50">{t("invest_cta")}</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const ModuleCard = ({ m, Icon, lang, liveLabel, soonLabel }) => {
  const info = m[lang];
  const inner = (
    <div className={`relative h-full p-7 qs-lift ${m.live ? "qs-grad-border" : "qs-panel"} ${!m.live ? "opacity-70" : ""}`}>
      {!m.live && (
        <div className="absolute top-5 right-5"><Lock className="h-4 w-4 text-slate-500" /></div>
      )}
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-11 w-11 rounded-sm" style={{ background: `${m.accent}1A` }}>
          <Icon className="h-6 w-6" style={{ color: m.accent }} />
        </span>
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${m.live ? "text-[#050A15]" : "text-slate-400 border border-white/10"}`}
          style={m.live ? { background: m.accent } : {}}>
          {m.live ? liveLabel : soonLabel}
        </span>
      </div>
      <h3 className="font-display font-bold text-xl mt-5">{info.name}</h3>
      <p className="text-sm text-slate-400 mt-2 leading-relaxed">{info.desc}</p>
    </div>
  );
  if (m.route) return <Link to={m.route} data-testid={`module-${m.id}`} className="block h-full">{inner}</Link>;
  if (m.id === "social-justice") return <Link to="/justice" data-testid={`module-${m.id}`} className="block h-full">{inner}</Link>;
  if (m.live && m.id !== "blockchain") return <Link to="/register" data-testid={`module-${m.id}`} className="block h-full">{inner}</Link>;
  if (m.id === "blockchain") return <Link to="/registry" data-testid={`module-${m.id}`} className="block h-full">{inner}</Link>;
  return <Link to={`/coming-soon/${m.id}`} data-testid={`module-${m.id}`} className="block h-full">{inner}</Link>;
};
